import React, { useState, useEffect } from 'react';
import { OperationVariables, QueryResult, useLazyQuery } from '@apollo/client';
import {
  TagPicker,
  TagPickerList,
  TagPickerInput,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  Tag,
  Field,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { GetCompaniesByFilter } from '../../../utils/statApi/CompaniesApi';
import ITag from '../../../utils/types/ITag';

interface ICompanyPickerCompany {
  id: number;
  companyCode: string;
  entityLegalName: string;
  lifecycleStatus?: string;
}

export interface ICompanyPickerTag extends ITag {
  companyCode?: string;
  lifecycleStatus?: string;
}

interface ICompanyPickerProps {
  selectedItems: ICompanyPickerTag[];
  setSelectedItems: (companyGroupCompanies: ICompanyPickerTag[]) => void;
  itemLimit?: number;
  required?: boolean;
  errorMessage?: string;
  label: string;
}

const CompanyPicker: React.FunctionComponent<ICompanyPickerProps> = ({
  selectedItems,
  setSelectedItems,
  itemLimit,
  required,
  errorMessage,
  label,
}: ICompanyPickerProps): JSX.Element => {
  const [loadCompanies] = useLazyQuery(GetCompaniesByFilter);
  const [availableOptions, setAvailableOptions] = useState<ICompanyPickerTag[]>([]);
  const [query, setQuery] = useState('');

  // Convert selectedItems to the format expected by TagPicker v9
  const selectedOptions = (selectedItems || []).map((item) => item.key.toString());

  // Load companies when query changes with debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      if (query.length > 0) {
        loadCompanies({
          variables: {
            companySearchDto: {
              keyword: query,
            },
            paginationDto: {
              currentPage: 1,
              pageSize: 10,
            },
          },
        }).then(
          (
            result: QueryResult<
              { companies: { items: ICompanyPickerCompany[] } },
              OperationVariables
            >,
          ) => {
            const companiesList: ICompanyPickerCompany[] = result?.data?.companies?.items || [];
            const options = companiesList.map((node) => ({
              key: node.id.toString(),
              name: `${node.companyCode} - ${node.entityLegalName}`,
              companyCode: node.companyCode,
              lifecycleStatus: node.lifecycleStatus,
            }));
            setAvailableOptions(options);
          },
        );
      } else {
        setAvailableOptions([]);
      }
    }, 300); // Debounce delay of 300ms

    return () => {
      clearTimeout(handler);
    };
  }, [query, loadCompanies]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (e, data) => {
    if (data.value === 'no-options') {
      return;
    }

    const resolveTag = (optionKey: string): ICompanyPickerTag | null => {
      const existing = selectedItems.find((item) => item.key === optionKey);
      if (existing) return existing;

      return availableOptions.find((item) => item.key === optionKey) ?? null;
    };

    // Single-select: replace the current selection with the newly chosen option
    if (itemLimit === 1) {
      const isDeselecting = selectedOptions.includes(data.value);
      const tag = isDeselecting ? null : resolveTag(data.value);
      setSelectedItems(tag ? [tag] : []);
      setQuery('');
      return;
    }

    const newSelectedOptions = [...data.selectedOptions];

    // Convert selected option keys back to full objects
    const selected = newSelectedOptions
      .map(resolveTag)
      .filter((item): item is ICompanyPickerTag => item !== null);

    setSelectedItems(selected);
    setQuery('');
  };

  // Filter options to exclude already selected items
  const filteredOptions = availableOptions.filter(
    (option) => !selectedOptions.includes(option.key.toString()),
  );

  return (
    <FluentProvider theme={webLightTheme}>
      <Field label={label} required={required} validationMessage={errorMessage}>
        <TagPicker onOptionSelect={onOptionSelect} selectedOptions={selectedOptions}>
          <TagPickerControl>
            <TagPickerGroup aria-label="Selected Companies">
              {(selectedItems || []).map((item) => (
                <Tag key={item.key} value={item.key.toString()}>
                  {item.name}
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput
              aria-label="Search Companies"
              placeholder="Search Companies"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </TagPickerControl>
          <TagPickerList>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <TagPickerOption value={option.key.toString()} key={option.key}>
                  {option.name}
                </TagPickerOption>
              ))
            ) : (
              <TagPickerOption value="no-options">No results found</TagPickerOption>
            )}
          </TagPickerList>
        </TagPicker>
      </Field>
    </FluentProvider>
  );
};

export default CompanyPicker;
